/**
 * Legal & Disputes — payment disputes waiting on a finding.
 *
 * Script 241 gave a tenant a way to file a dispute and a landlord a way to
 * answer one, and said that only Orchid settles it. Script 242 built the two
 * functions. This screen is the last piece: until it existed, a dispute
 * carried both parties' testimony and NOBODY AT ORCHID COULD SEE IT, so the
 * outcome column could never be written by anyone, by any route. A tenant would
 * have filed into silence.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS DESK MAY STILL BE EMPTY FOR EVERYONE, AND THAT IS NOT A BUG HERE
 *
 * `resolve_dispute` is in the permissions catalogue — "Adjudicate payment,
 * deposit and invoice disputes" — and is held by NOBODY. Measured: of six
 * active staff, exactly one reaches it, and only through the `'all'` wildcard.
 * The table's own RLS policy meanwhile gates on `fica_review`, which is in no
 * row of the catalogue at all: the same shape as `manage_sanctions_list` before
 * script 205 registered it, and row two of the ledger's thirteen.
 *
 * Both 242 functions gate on `resolve_dispute`, so nothing here depends on the
 * ghost. But granting somebody the permission is a founder decision, and until
 * it is made this screen will render an empty queue for every desk except the
 * wildcard holder — which is exactly the failure mode the sanctions screen
 * documented, so it is said here before somebody spends an afternoon on it.
 *
 * ---------------------------------------------------------------------------
 * THE REASON, BEFORE THE RECORD
 *
 * The queue carries what each party wrote about a payment: identifiable
 * personal data joined to a grievance. So the ReasonGate stands in front of the
 * fetch. It is not decoration — `fn_desk_payment_disputes` calls
 * `fn_log_my_admin_action` as its FIRST statement, so the log is a condition of
 * the disclosure and a blank reason never reaches a row (Canon 4.2).
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS SCREEN REFUSES TO DO
 *
 * There is no third outcome, no way to edit a finding once made, and no control
 * for the score protection — that is DERIVED from the finding, in the database,
 * because a desk who could set it independently would be declaring an
 * eligibility beside the one they recorded (Immutable Rule 4).
 *
 * And no party's name appears. The queue names the unit. Prohibition 6 applies
 * to a desk queue as much as to any other search surface, and a desk who needs
 * to know who is involved opens the subject lens, with its own reason.
 */

import { useCallback, useState } from 'react';
import {
  O, SANS, Card, StatusPill, LoadingPanel, Notice, PrimaryCTA, QuietButton,
  Field, textInputStyle,
} from '../ui';
import ReasonGate from '../ReasonGate';
import {
  fetchDisputeQueue, settleDispute, FINDINGS, disputeTypeLabel,
  fetchExitDisputeQueue, settleExitDispute, EXIT_FINDINGS, exitCategoryLabel,
} from '../lib/disputes';

const PERMISSION = {
  permission_key: 'resolve_dispute',
  description: 'Adjudicate payment, deposit, invoice and exit-rating disputes.',
};

/** The database refuses a blank; saying so up front is kinder than a refusal. */
const MIN_SUMMARY = 12;

const STATES = {
  open: { label: 'Open', tone: 'critical' },
  grace_window: { label: 'In grace window', tone: 'warning' },
  under_review: { label: 'Under review', tone: 'warning' },
};

const Line = ({ label, children }) => (
  <div style={{ display: 'flex', gap: 14, padding: '7px 0', fontFamily: SANS, fontSize: 12.5 }}>
    <span style={{ width: 132, flexShrink: 0, color: O.inkMuted }}>{label}</span>
    <span style={{ color: O.ink, minWidth: 0 }}>{children || '—'}</span>
  </div>
);

/** One dispute, and the two things that can be said about it. */
function Dispute({ row, onSettled }) {
  const [finding, setFinding] = useState(null);
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [done, setDone] = useState(null);

  const tooShort = summary.trim().length < MIN_SUMMARY;
  const state = STATES[row.state] || { label: row.state, tone: 'quiet' };

  const submit = async () => {
    setBusy(true);
    setProblem(null);
    const res = await settleDispute({
      disputeId: row.disputeId,
      finding,
      summary: summary.trim(),
      // The desk already gave a reason to open the queue; recording a finding
      // is a second act of access and carries its own, as the log demands.
      reason: `Recording a finding on dispute ${row.reference || row.disputeId}`,
    });
    setBusy(false);
    if (res.error) { setProblem(res.error); return; }
    setDone(res.data?.message || 'Recorded.');
    onSettled();
  };

  return (
    <Card style={{ marginBottom: 14 }}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 14, marginBottom: 10,
      }}>
        <div style={{ minWidth: 0 }}>
          <p style={{
            fontFamily: SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.09em',
            textTransform: 'uppercase', color: O.inkFaint, margin: '0 0 5px',
          }}>{row.reference || 'No reference'}</p>
          <p style={{ fontFamily: SANS, fontSize: 15, fontWeight: 700, color: O.ink, margin: 0 }}>
            {disputeTypeLabel(row.disputeType)}
          </p>
        </div>
        {/* How long somebody has been waiting is what a desk triages on, and the
            queue is ordered oldest-first by the database for the same reason. */}
        <StatusPill tone={state.tone}>
          {state.label} · day {row.daysOpen}
        </StatusPill>
      </div>

      <div style={{ borderTop: `1px solid ${O.hairline}`, paddingTop: 8 }}>
        <Line label="Unit">{row.propertyReference}</Line>
        <Line label="Filed">{row.filedAt ? String(row.filedAt).slice(0, 10) : null}</Line>
        {row.graceWindowEnds && (
          <Line label="Held open until">{String(row.graceWindowEnds).slice(0, 10)}</Line>
        )}
      </div>

      {/* BOTH ACCOUNTS, SIDE BY SIDE AND UNEDITABLE. Neither party can change
          the other's words — the database refuses it — and neither can this
          desk. What is written is what was written. */}
      <div style={{ marginTop: 12, borderTop: `1px solid ${O.hairline}`, paddingTop: 12 }}>
        <p style={{
          fontFamily: SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.09em',
          textTransform: 'uppercase', color: O.inkFaint, margin: '0 0 6px',
        }}>The tenant said</p>
        <p style={{ fontFamily: SANS, fontSize: 13, color: O.ink, margin: 0, lineHeight: 1.65 }}>
          {row.tenantExplanation}
        </p>

        <p style={{
          fontFamily: SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.09em',
          textTransform: 'uppercase', color: O.inkFaint, margin: '14px 0 6px',
        }}>The landlord answered</p>
        <p style={{
          fontFamily: SANS, fontSize: 13, margin: 0, lineHeight: 1.65,
          color: row.landlordResponse ? O.ink : O.inkMuted,
        }}>
          {row.landlordResponse
            || 'Nothing yet. A finding can still be made — the landlord was given the chance.'}
        </p>
      </div>

      {done ? (
        <div style={{ marginTop: 12 }}><Notice tone="success">{done}</Notice></div>
      ) : (
        <div style={{ marginTop: 14, borderTop: `1px solid ${O.hairline}`, paddingTop: 14 }}>
          {problem && <Notice tone="critical">{problem}</Notice>}

          <p style={{
            fontFamily: SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.09em',
            textTransform: 'uppercase', color: O.inkFaint, margin: '0 0 8px',
          }}>Your finding</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {FINDINGS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFinding(f.key)}
                style={{
                  textAlign: 'left', cursor: 'pointer', font: 'inherit',
                  background: finding === f.key ? O.tintGreen || 'rgba(10,43,29,0.06)' : 'transparent',
                  border: `1px solid ${finding === f.key ? O.ink : O.hairline}`,
                  borderRadius: 3, padding: '10px 12px',
                }}
                aria-pressed={finding === f.key}
              >
                <span style={{
                  display: 'block', fontFamily: SANS, fontSize: 13.5, fontWeight: 700,
                  color: O.ink, marginBottom: 3,
                }}>{f.label}</span>
                <span style={{
                  display: 'block', fontFamily: SANS, fontSize: 12, color: O.inkMuted,
                  lineHeight: 1.55,
                }}>{f.blurb}</span>
              </button>
            ))}
          </div>

          <Field
            label="What you found"
            hint="Both parties read this. It is the record of why the dispute ended the way it did."
          >
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={4}
              style={{ ...textInputStyle(false), resize: 'vertical' }}
            />
          </Field>

          <div style={{ display: 'flex', gap: 9, marginTop: 12 }}>
            <QuietButton
              onClick={() => { setFinding(null); setSummary(''); setProblem(null); }}
              disabled={busy}
            >
              Clear
            </QuietButton>
            <PrimaryCTA onClick={submit} busy={busy} disabled={busy || !finding || tooShort}>
              Record the finding
            </PrimaryCTA>
          </div>

          {(!finding || tooShort) && (
            <p style={{
              fontFamily: SANS, fontSize: 11.5, color: O.inkMuted, margin: '8px 0 0',
            }}>
              {!finding
                ? 'Choose which way it went.'
                : `A few more words — ${MIN_SUMMARY - summary.trim().length} to go.`}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

/**
 * One exit-rating dispute (script 368), and the two findings a desk can make.
 *
 * The consequence is derived, as with a payment dispute: the queue row says
 * how many of the tenant's OTHER tenancies rated this category, because that
 * is what an upheld finding will draw a median from -- and a desk should know
 * before pressing that zero means the category is left out, not re-rated.
 */
function ExitDispute({ row, onSettled }) {
  const [finding, setFinding] = useState(null);
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [done, setDone] = useState(null);

  const tooShort = summary.trim().length < MIN_SUMMARY;

  const submit = async () => {
    setBusy(true);
    setProblem(null);
    const res = await settleExitDispute({
      disputeId: row.disputeId,
      finding,
      summary: summary.trim(),
      reason: `Recording a finding on exit-rating dispute ${row.reference || row.disputeId}`,
    });
    setBusy(false);
    if (res.error) { setProblem(res.error); return; }
    setDone(res.data?.message || 'Recorded.');
    onSettled();
  };

  return (
    <Card style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                    gap: 14, marginBottom: 10 }}>
        <div style={{ minWidth: 0 }}>
          <p style={{ fontFamily: SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.09em',
                      textTransform: 'uppercase', color: O.inkFaint, margin: '0 0 5px' }}>
            {row.reference || 'No reference'}
          </p>
          <p style={{ fontFamily: SANS, fontSize: 15, fontWeight: 700, color: O.ink, margin: 0 }}>
            {exitCategoryLabel(row.category)}
          </p>
        </div>
        <StatusPill tone="critical">Open · day {row.daysOpen}</StatusPill>
      </div>

      <div style={{ borderTop: `1px solid ${O.hairline}`, paddingTop: 8 }}>
        <Line label="Unit">{row.propertyReference}</Line>
        <Line label="Filed">{row.filedAt ? String(row.filedAt).slice(0, 10) : null}</Line>
        <Line label="If upheld">
          {row.otherTenanciesRated > 0
            ? `re-read from ${row.otherTenanciesRated} other tenanc${row.otherTenanciesRated === 1 ? 'y' : 'ies'}`
            : 'left out of this tenancy (no other tenancy to draw on)'}
        </Line>
      </div>

      <div style={{ marginTop: 12, borderTop: `1px solid ${O.hairline}`, paddingTop: 12 }}>
        <p style={{ fontFamily: SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.09em',
                    textTransform: 'uppercase', color: O.inkFaint, margin: '0 0 6px' }}>The tenant said</p>
        <p style={{ fontFamily: SANS, fontSize: 13, color: O.ink, margin: 0, lineHeight: 1.65 }}>
          {row.tenantExplanation}
        </p>
        <p style={{ fontFamily: SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.09em',
                    textTransform: 'uppercase', color: O.inkFaint, margin: '14px 0 6px' }}>The landlord answered</p>
        <p style={{ fontFamily: SANS, fontSize: 13, margin: 0, lineHeight: 1.65,
                    color: row.landlordResponse ? O.ink : O.inkMuted }}>
          {row.landlordResponse
            || 'Nothing yet. A finding can still be made — the landlord was given the chance.'}
        </p>
      </div>

      {done ? (
        <div style={{ marginTop: 12 }}><Notice tone="success">{done}</Notice></div>
      ) : (
        <div style={{ marginTop: 14, borderTop: `1px solid ${O.hairline}`, paddingTop: 14 }}>
          {problem && <Notice tone="critical">{problem}</Notice>}
          <p style={{ fontFamily: SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.09em',
                      textTransform: 'uppercase', color: O.inkFaint, margin: '0 0 8px' }}>Your finding</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {EXIT_FINDINGS.map((f) => (
              <button key={f.key} type="button" onClick={() => setFinding(f.key)}
                      aria-pressed={finding === f.key}
                      style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit',
                               background: finding === f.key ? O.tintGreen || 'rgba(10,43,29,0.06)' : 'transparent',
                               border: `1px solid ${finding === f.key ? O.ink : O.hairline}`,
                               borderRadius: 3, padding: '10px 12px' }}>
                <span style={{ display: 'block', fontFamily: SANS, fontSize: 13.5, fontWeight: 700,
                               color: O.ink, marginBottom: 3 }}>{f.label}</span>
                <span style={{ display: 'block', fontFamily: SANS, fontSize: 12, color: O.inkMuted,
                               lineHeight: 1.55 }}>{f.blurb}</span>
              </button>
            ))}
          </div>
          <Field label="What you found"
                 hint="Both parties read this. It is the record of why the dispute ended the way it did.">
            <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={4}
                      aria-label="What you found about the exit rating"
                      style={{ ...textInputStyle(false), resize: 'vertical' }} />
          </Field>
          <div style={{ display: 'flex', gap: 9, marginTop: 12 }}>
            <QuietButton onClick={() => { setFinding(null); setSummary(''); setProblem(null); }} disabled={busy}>
              Clear
            </QuietButton>
            <PrimaryCTA onClick={submit} busy={busy} disabled={busy || !finding || tooShort}>
              Record the finding
            </PrimaryCTA>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function DisputeQueue() {
  const [rows, setRows] = useState(null);
  const [exitRows, setExitRows] = useState([]);
  const [problem, setProblem] = useState(null);
  const [reason, setReason] = useState(null);

  const load = useCallback(async (withReason) => {
    const [res, ex] = await Promise.all([fetchDisputeQueue(withReason), fetchExitDisputeQueue(withReason)]);
    if (res.error) { setProblem(res.error); setRows([]); return; }
    // The exit queue failing is said, and the payment queue still draws.
    setProblem(ex.error || null);
    setRows(res.data);
    setExitRows(ex.data || []);
  }, []);

  // THE GATE IS BEFORE THE FETCH, NOT BESIDE IT.
  //
  // INLINE for the same reason SanctionsReview is: this section mounts with the
  // card, and the modal it used to raise had `onClose={() => {}}` -- a Cancel
  // that did nothing, on an overlay covering the whole page.
  if (!reason) {
    return (
      <ReasonGate
        permission={PERMISSION}
        inline
        onProceed={(r) => { setReason(r); load(r); }}
      />
    );
  }

  if (rows === null) return <LoadingPanel message="Opening the dispute queue" />;

  return (
    <div>
      {problem && <Notice tone="critical">{problem}</Notice>}

      {/* AN EMPTY QUEUE IS A SUCCESS. Nobody is waiting on a finding, and the
          copy says so rather than inventing something to look at. */}
      {!rows.length && !exitRows.length ? (
        <Card>
          <p style={{ fontFamily: SANS, fontSize: 13.5, color: O.ink, margin: 0, lineHeight: 1.65 }}>
            No dispute is waiting on Orchid. That is the good state.
          </p>
          <p style={{ fontFamily: SANS, fontSize: 12.5, color: O.inkMuted, margin: '8px 0 0', lineHeight: 1.6 }}>
            If you expected something here, check that your desk holds
            <strong> resolve_dispute</strong>. It is in the permissions catalogue and,
            measured on 2 September 2026, was granted to nobody — so an empty queue
            may mean the permission has not been handed out rather than that nothing
            was filed.
          </p>
        </Card>
      ) : (
        <>
          {rows.map((r) => (
            <Dispute key={r.disputeId} row={r} onSettled={() => load(reason)} />
          ))}
          {exitRows.length > 0 && (
            <p style={{ fontFamily: SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.09em',
                        textTransform: 'uppercase', color: O.inkFaint, margin: '18px 0 10px' }}>
              Exit ratings
            </p>
          )}
          {exitRows.map((r) => (
            <ExitDispute key={r.disputeId} row={r} onSettled={() => load(reason)} />
          ))}
        </>
      )}
    </div>
  );
}
